import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import moment from 'moment';
import { useClonePreview, useCloneDocument } from 'entities/document';
import CloneDocumentModal from 'features/document-hub/CloneDocumentModal';
import Spinner from 'shared/feedback/Spinner';
import StepCarries from './StepCarries';
import StepPeriods from './StepPeriods';
import StepOptions from './StepOptions';
import StepReview from './StepReview';
import './RollForwardWizard.scss';
import { Typography } from 'shared/ui/Typography/Typography';

const TOTAL_STEPS = 4;
const DATE_FMT = 'YYYY-MM-DD';

const toDate = (value: string | null | undefined) => (value ? moment(value).format(DATE_FMT) : null);
const shiftDate = (value: string | null | undefined) => (value ? moment(value, DATE_FMT).add(1, 'year').format(DATE_FMT) : value);

type PeriodField = 'periodStartDate' | 'periodEndDate' | 'periodInstant';

interface PeriodValues {
  periodStartDate?: string | null;
  periodEndDate?: string | null;
  periodInstant?: string | null;
}

interface PreviewContext {
  contextId: string;
  periodForever?: boolean;
  periodStartDate?: string | null;
  periodEndDate?: string | null;
  periodInstant?: string | null;
}

interface ClonePreview {
  tagCount: number;
  unitCount: number;
  contexts: PreviewContext[];
}

interface DocumentVersion {
  documentSuperId: number | string;
  documentRevisionId?: string;
}

interface RollForwardWizardProps {
  show: boolean;
  documentVersion?: DocumentVersion;
  selectedOrganization: unknown;
  onClose: () => void;
}

const RollForwardWizard = ({ show, documentVersion, selectedOrganization, onClose }: RollForwardWizardProps) => {
  const { t } = useTranslation('roll-forward');

  const documentSuperId = documentVersion?.documentSuperId ?? '';
  const documentRevisionId = documentVersion?.documentRevisionId;

  const { data: preview, isLoading } = useClonePreview(documentSuperId, documentRevisionId) as { data: ClonePreview; isLoading: boolean };
  const { mutate: cloneDocument, isPending: isCloning, isSuccess, isError } = useCloneDocument();

  const [step, setStep] = useState(1);
  const [periods, setPeriods] = useState<Record<string, PeriodValues>>({});
  const [keepDismissals, setKeepDismissals] = useState(false);

  const editableContexts = useMemo(() => (preview?.contexts ?? []).filter((c) => !c.periodForever), [preview]);

  useEffect(() => {
    const init: Record<string, PeriodValues> = {};
    (preview?.contexts ?? []).forEach((c) => {
      init[c.contextId] = { periodStartDate: toDate(c.periodStartDate), periodEndDate: toDate(c.periodEndDate), periodInstant: toDate(c.periodInstant) };
    });
    setPeriods(init);
    setStep(1);
    setKeepDismissals(false);
  }, [preview]);

  useEffect(() => {
    if (isSuccess || isError) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  if (!isLoading && (!preview || preview.tagCount === 0)) {
    return <CloneDocumentModal show={show} documentVersion={documentVersion} selectedOrganization={selectedOrganization} onClose={onClose} />;
  }

  const loading = isLoading || !preview;

  const setContextField = (contextId: string, field: PeriodField, value: string | null) =>
    setPeriods((prev) => ({ ...prev, [contextId]: { ...prev[contextId], [field]: value } }));

  const shiftAll = () =>
    setPeriods((prev) => {
      const next = { ...prev };
      editableContexts.forEach((c) => {
        const cur = prev[c.contextId] ?? {};
        next[c.contextId] = {
          periodStartDate: shiftDate(cur.periodStartDate),
          periodEndDate: shiftDate(cur.periodEndDate),
          periodInstant: shiftDate(cur.periodInstant),
        };
      });
      return next;
    });

  const buildPeriodOverrides = () => {
    const overrides: Record<string, PeriodValues> = {};
    editableContexts.forEach((c) => {
      const cur = periods[c.contextId] ?? {};
      const changed =
        cur.periodStartDate !== toDate(c.periodStartDate) || cur.periodEndDate !== toDate(c.periodEndDate) || cur.periodInstant !== toDate(c.periodInstant);
      if (changed) {
        overrides[c.contextId] = {
          periodStartDate: cur.periodStartDate ?? null,
          periodEndDate: cur.periodEndDate ?? null,
          periodInstant: cur.periodInstant ?? null,
        };
      }
    });
    return overrides;
  };

  const onClone = () => {
    cloneDocument({
      documentSuperId,
      documentRevisionId,
      selectedOrganization,
      rollForward: { periodOverrides: buildPeriodOverrides(), keepDismissals },
    });
  };

  const body = () => {
    if (step === 1) return <StepCarries preview={preview} />;
    if (step === 2) return <StepPeriods contexts={editableContexts} periods={periods} onChange={setContextField} onShiftAll={shiftAll} />;
    if (step === 3) return <StepOptions keepDismissals={keepDismissals} onChange={setKeepDismissals} />;
    return <StepReview preview={preview} periodOverrides={buildPeriodOverrides()} keepDismissals={keepDismissals} />;
  };

  return (
    <Modal show={show} onHide={onClose} animation={false} dialogClassName="roll-forward-dialog" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <Typography variant="body-8" fontWeight="semibold">
            {t('title')} — {t('step-counter')} {step}/{TOTAL_STEPS}
          </Typography>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="roll-forward__loading">
            <Spinner />
          </div>
        ) : (
          body()
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t('cancel')}
        </Button>
        {!loading && step > 1 ? (
          <Button variant="secondary" onClick={() => setStep((s) => s - 1)}>
            {t('back')}
          </Button>
        ) : null}
        {loading || step < TOTAL_STEPS ? (
          <Button variant="primary" disabled={loading} onClick={() => setStep((s) => s + 1)}>
            {t('next')}
          </Button>
        ) : (
          <Button variant="primary" disabled={isCloning} onClick={onClone}>
            {isCloning ? <i className="fa-regular fa-circle-notch fa-spin mr-2" /> : null}
            {t('clone')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default RollForwardWizard;
